import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FeatureLink } from '../../components/FeatureLink';
import { Send, ArrowRight, Boxes, UserCheck } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import { useParticipant } from '../../hooks/useParticipant';
import { usePermissions } from '../../hooks/usePermissions';
import { useOpenWorkspace } from '../../hooks/useOpenWorkspace';
import type { AaiRequest } from '../../types/aai';
import { Loading, ErrorNote } from './components';
import { Card, MonoLabel, RiskPill, StatusPill, PageHeader } from './dashboards/ui';

const DECISION_BTN: Record<string, { label: string; cls: string }> = {
  approved: { label: 'Approve', cls: 'bg-brand-500 hover:bg-brand-600 text-white' },
  needs_info: { label: 'Ask more', cls: 'bg-gray-100 hover:bg-gray-200 text-gray-700' },
  declined: { label: 'Decline', cls: 'text-red-600 hover:bg-red-50' },
};

// Final-approval governance fields recorded on the use-case registry.
const MODEL_OPTS = ['Claude (Opus)', 'Claude (Sonnet)', 'Claude (Haiku)', 'GPT-4o'];
const HIL_OPTS = ['Human approves each output', 'Output reviewed', 'Spot-checked', 'Fully autonomous'];

// The request pipeline (design's top card) — the two-gate chain end to end.
// A horizontal stage explainer; each request's live position shows on its row.
const PIPELINE = [
  { label: 'Business case', who: 'You describe it' },
  { label: 'Dept head', who: 'Gate 1' },
  { label: 'Champion', who: 'Gate 2' },
  { label: 'Scoped → PRD', who: 'Auto' },
  { label: 'Marketplace', who: 'Build' },
];

// What Orbit recommended we BUILD. Not the size (that is the route) and not what
// shipped (that is read from a published skill). A request with nothing recorded
// says so rather than guessing the commonest answer.
const SHAPE_LABEL: Record<string, string> = {
  skill: 'Skill', plugin: 'Plugin', document: 'Document', routine: 'Routine',
};

/* The shape, and why it is that shape.
 *
 * The pill alone told the Owner what they were approving and not why, which is
 * the half that decides whether the recommendation is right. The reason is the
 * sentence the intake rules gave when the request was raised; it is stored per
 * request, so a row raised before that was kept shows the pill on its own
 * rather than a sentence invented for it now. */
const ShapePill: React.FC<{ deliverable?: string | null; reason?: string | null }> = ({ deliverable, reason }) => {
  const label = SHAPE_LABEL[deliverable || ''];
  if (!label) return null;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-semibold text-brand-700">
        {label}
      </span>
      {reason && <span className="max-w-[30ch] text-[11px] leading-[17px] text-gray-500">{reason}</span>}
    </span>
  );
};

const ROLE_COPY: Record<'scoper' | 'builder', { one: string; none: string; add: string }> = {
  scoper: {
    one: 'is scoping it',
    none: 'Nobody is scoping this yet',
    add: 'name a scoper',
  },
  builder: {
    one: 'is building it',
    none: 'Nobody is building this yet',
    add: 'name a builder',
  },
};

/*
 * Everyone on one job, and the two ways it moves: hand it to somebody else, or
 * step off it. Both roles draw the same because they are the same shape of fact.
 *
 * "Nobody yet" is only worth saying once a decision has been taken; before that
 * there is nothing to be on, and saying so on every pending row would be noise.
 */
const RosterLine: React.FC<{
  req: AaiRequest;
  role: 'scoper' | 'builder';
  canStaff: boolean;
  onAdd: (r: AaiRequest, role: 'scoper' | 'builder') => void;
  onHand: (r: AaiRequest, role: 'scoper' | 'builder', from: string) => void;
  onDrop: (id: string, email: string, role: 'scoper' | 'builder') => void;
  busy: boolean;
}> = ({ req, role, canStaff, onAdd, onHand, onDrop, busy }) => {
  const people = (role === 'scoper' ? req.scopers : req.builders) || [];
  const copy = ROLE_COPY[role];
  const decided = req.status === 'approved' || req.status === 'dept_approved';

  if (people.length === 0) {
    if (!decided) return null;
    return (
      <div className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700">
        <UserCheck size={11} />
        {copy.none}
        {canStaff && (
          <button onClick={() => onAdd(req, role)} disabled={busy}
            className="ml-1 font-semibold text-brand-700 hover:underline disabled:opacity-50">{copy.add}</button>
        )}
      </div>
    );
  }

  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5">
      {people.map((person) => (
        <span key={person.email}
          className="inline-flex items-center gap-1.5 rounded-full bg-gray-50 px-2 py-0.5 text-[11px] text-gray-600">
          <UserCheck size={11} className="text-emerald-600" />
          {person.email} {copy.one}
          {canStaff && (
            <>
              <button onClick={() => onHand(req, role, person.email)} disabled={busy}
                className="ml-1 font-semibold text-brand-600 hover:underline disabled:opacity-50">hand over</button>
              <button onClick={() => onDrop(req.id, person.email, role)} disabled={busy}
                className="font-semibold text-gray-500 hover:underline disabled:opacity-50">remove</button>
            </>
          )}
        </span>
      ))}
      {canStaff && (
        <button onClick={() => onAdd(req, role)} disabled={busy}
          className="text-[11px] font-semibold text-brand-600 hover:underline disabled:opacity-50">
          + another
        </button>
      )}
    </div>
  );
};

const panelParams = (route?: string): URLSearchParams | null => {
  if (!route) return null;
  return new URLSearchParams(route.split('?')[1] || '');
};

const RequestsPage: React.FC<{ panelRoute?: string }> = ({ panelRoute }) => {
  const { context } = useParticipant();
  const { can } = usePermissions();
  // I4: granted by being in a department marked as one that builds, not by role.
  const canBuildDirectly = can('aai_build_directly');
  const caps = context?.capabilities;
  // aai_approve_requests, not "can view the company dashboard". An expert holds
  // the latter for their own overview page, so borrowing it here put the approve
  // buttons and the assign control in front of the one person whose job is the
  // engineering pass on work somebody else approved.
  const isCompany = can('aai_approve_requests');
  // Scoping is the department head's to hand out as well: the call was explicit
  // that a request short on detail can go to the head to be filled in.
  const canStaffScoping = isCompany || can('aai_view_department_rollup');
  const canDecide = !!caps?.view_department_rollup || isCompany;
  const me = (localStorage.getItem('user_email') || '').toLowerCase();
  /* Who may carry on scoping, read the same way the server reads it: whoever
     approves, the department head, and the person who raised it. It used to ask
     for `view_company_dashboard`, which no department head holds, so the head
     was offered no door to a request the server would have let them scope. */
  const mayScope = (r: AaiRequest): boolean => {
    if (canStaffScoping) return true;
    return (r.requester_email || '').toLowerCase() === me;
  };
  const isDeptHead = !!caps?.view_department_rollup;

  // Which decisions the caller may take, given the strict two-gate chain. Gate 1
  // of a departmented request is the dept head's; deptless gate-1 and gate-2
  // (dept_approved) belong to a champion/owner. The backend enforces dept-pinning
  // and gate order — this only decides which buttons to render.
  const actionsFor = (r: AaiRequest): string[] => {
    if (r.status === 'pending' || r.status === 'needs_info') {
      const deptless = !(r.department && r.department.trim());
      if (deptless) return isCompany ? ['approved', 'needs_info', 'declined'] : [];
      return isDeptHead ? ['approved', 'needs_info', 'declined'] : [];
    }
    if (r.status === 'dept_approved') return isCompany ? ['approved', 'declined'] : [];
    return [];
  };
  // A dept head's "approve" is gate 1 — it forwards, it doesn't grant. Label it so.
  const decisionLabel = (r: AaiRequest, d: string) =>
    d === 'approved' && isDeptHead && !isCompany && (r.status === 'pending' || r.status === 'needs_info')
      ? 'Forward to champion'
      : DECISION_BTN[d].label;

  const [requests, setRequests] = useState<AaiRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // I1: the marketplace sends people here when nothing matched, carrying what
  // they searched for. Asking them to describe it a second time is how a loop
  // stops feeling joined up.
  const [urlParams] = useSearchParams();
  // In a panel there is no URL to read the prefilled title out of.
  const searchParams = panelParams(panelRoute) || urlParams;
  const [title, setTitle] = useState(() => searchParams.get('title') || '');
  const [detail, setDetail] = useState('');
  const cameFromMarketplace = !!searchParams.get('title');
  const [busy, setBusy] = useState(false);
  const { opening: crossing, open: openWorkspace } = useOpenWorkspace();
  // Final-gate approval modal — capture model + human-in-loop for the registry.
  const [approving, setApproving] = useState<AaiRequest | null>(null);
  const [mdl, setMdl] = useState('Claude (Sonnet)');
  const [hil, setHil] = useState('Output reviewed');
  // I2: who will actually do it. Loaded once for the company gate, because a
  // picker that offers people who cannot open the work is worse than no picker.
  const [experts, setExperts] = useState<{ email: string; name: string }[]>([]);
  const [assignee, setAssignee] = useState('');
  // Decline / ask-more reason modal — the reason rides the requester's notification.
  const [reasoning, setReasoning] = useState<{ req: AaiRequest; decision: string } | null>(null);
  // I2: who is doing this changes after approval more often than the approval
  // does. People leave, load moves, an assignment made at the gate turns out
  // wrong. Without this, a request approved with "Decide later" could never be
  // given to anybody.
  const [assigning, setAssigning] = useState<AaiRequest | null>(null);
  // Which job the modal is naming. 'builder' keeps the old behaviour exactly;
  // 'scoper' is the person who fills in what the request could not say.
  const [rosterRole, setRosterRole] = useState<'scoper' | 'builder'>('builder');
  // Set when somebody already holds the job, which makes this a handover rather
  // than a first assignment. Both go through the same modal because they are the
  // same decision: who has this now.
  const [handingFrom, setHandingFrom] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  // Marketplace "reuse before build" hints, keyed by request id.
  const [hints, setHints] = useState<Record<string, { match_count: number; top_match: string | null }>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const reqs = (await aaiApi.listRequests()).data.requests;
      setRequests(reqs); setError(null);
      const approved = reqs.filter((r) => r.status === 'approved' && r.problem_id);
      void Promise.all(approved.map((r) =>
        aaiApi.getMarketplaceHint(r.id).then((res) => [r.id, res.data] as const).catch(() => null),
      )).then((pairs) => {
        const m: Record<string, { match_count: number; top_match: string | null }> = {};
        for (const p of pairs) if (p) m[p[0]] = p[1];
        setHints(m);
      });
    }
    catch { setError('Could not load requests.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isCompany) return;
    aaiApi.assignableExperts()
      .then(({ data }) => setExperts(data.experts))
      .catch(() => setExperts([]));
  }, [isCompany]);

  const submit = async () => {
    if (!title.trim() || busy) return;
    setBusy(true);
    try { await aaiApi.createRequest({ title: title.trim(), detail: detail.trim() || undefined }); setTitle(''); setDetail(''); await load(); }
    catch { setError('Could not submit your request.'); }
    finally { setBusy(false); }
  };

  const decide = async (id: string, decision: string,
                        opts?: { model?: string; human_in_loop?: string; comment?: string;
                                 assigned_expert_email?: string }) => {
    setBusy(true);
    try {
      await aaiApi.decideRequest(id, { decision, ...opts });
      window.dispatchEvent(new Event('aai:approvals-changed'));
      setError(null); setApproving(null); setReasoning(null); setReason('');
      await load();
    }
    catch (e: any) { setError(e?.response?.data?.detail || 'Could not record that decision.'); }
    finally { setBusy(false); }
  };

  // A final-gate approval (company role) opens the governance modal; a decline or
  // ask-more opens a reason modal (the reason is relayed to the requester in
  // their notification — F3: "notifies the requester with the reason"). A
  // dept-head forward decides immediately.
  const onAct = (r: AaiRequest, d: string) => {
    if (d === 'approved' && isCompany) { setMdl('Claude (Sonnet)'); setHil('Output reviewed'); setAssignee(r.assigned_expert_email || ''); setApproving(r); }
    else if (d === 'declined' || d === 'needs_info') { setReason(''); setReasoning({ req: r, decision: d }); }
    else decide(r.id, d);
  };

  const closeAssign = () => { setAssigning(null); setAssignee(''); setHandingFrom(null); };

  const staff = async (id: string, email: string) => {
    setBusy(true);
    try {
      if (handingFrom) {
        await aaiApi.transferRequestAssignee(id, { to_email: email, role: rosterRole, from_email: handingFrom });
      } else {
        await aaiApi.addRequestAssignee(id, email, rosterRole);
      }
      setError(null); closeAssign();
      await load();
    }
    catch (e: any) { setError(e?.response?.data?.detail || 'Could not record who is on it.'); }
    finally { setBusy(false); }
  };

  const drop = async (id: string, email: string, role: 'scoper' | 'builder') => {
    setBusy(true);
    try {
      await aaiApi.removeRequestAssignee(id, email, role);
      setError(null);
      await load();
    }
    catch (e: any) { setError(e?.response?.data?.detail || 'Could not take them off.'); }
    finally { setBusy(false); }
  };

  /* The single scoping door. This row used to link to /problems/<id>, which is
     this app's own scoping screen: the second flow the call asked us to stop
     keeping alive. The list and the status stay here, the scoping itself moves
     to the Workspace, and the conversation is pinned per problem so this picks
     up where it stopped rather than starting a rival one. */
  const carryOnScoping = (r: AaiRequest) => openWorkspace(
    r.company_id || localStorage.getItem('client_id'),
    async () => (await aaiApi.scopeInWorkspace(r.id)).data.path,
  );

  const openStaffing = (r: AaiRequest, role: 'scoper' | 'builder', from?: string) => {
    setRosterRole(role);
    setHandingFrom(from || null);
    setAssignee('');
    setAssigning(r);
  };

  let staffingTitle = 'Who will build it';
  let staffingNote = 'They are told, and the requester can see who has it. The build document goes to them alone instead of to every expert.';
  let staffingAction = 'Assign';
  if (rosterRole === 'scoper') {
    staffingTitle = 'Who will scope it';
    staffingNote = 'A first request rarely says enough to build from. Whoever you name fills in the rest, and can hand it on themselves.';
  }
  if (handingFrom) {
    staffingTitle = 'Hand it to somebody else';
    staffingNote = `${handingFrom} steps off and whoever you pick takes it on. Both are kept on the record, so the handover is not lost.`;
    staffingAction = 'Hand over';
  }

  // The request form + how-it-travels rail (everyone can raise a request).
  const requestForm = (
    <div className="grid grid-cols-1 md:grid-cols-[1.1fr_1fr] gap-4 mb-5">
      <div className="bg-white border border-gray-100 rounded-xl p-5">
        <div className="font-bold text-sm mb-1">Request an AI tool</div>
        <p className="text-[12px] text-gray-500 mb-3">
          Describe a problem worth solving — if it's approved, a tool gets built for it.
          Need access to a tool that already exists? <FeatureLink to="/aai/skills" className="text-brand-600 hover:underline">Find it in the Skills &amp; Plugins Marketplace</FeatureLink>.
        </p>
        {canBuildDirectly && (
          <p className="mb-3 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-[12px] text-gray-700">
            Your department builds things, so you do not have to wait for this one.
            {' '}<FeatureLink to="/my-business" className="font-semibold text-emerald-700 hover:underline">Scope it yourself</FeatureLink>.
          </p>
        )}
        {cameFromMarketplace && (
          <p className="mb-3 rounded-lg bg-brand-50 border border-brand-100 px-3 py-2 text-[12px] text-gray-700">
            Nobody had built this, so we brought your words over. Add anything that would help.
          </p>
        )}
        <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1">What do you want to solve?</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Summarize weekly loan memos"
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-brand-200" />
        <textarea value={detail} onChange={(e) => setDetail(e.target.value)} rows={3} placeholder="Describe the problem in plain words…"
          className="w-full rounded-lg border border-gray-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200" />
        <div className="mt-3 flex items-center justify-between gap-3">
          <span className="text-[11.5px] text-gray-400">Routes to your department head → champion.</span>
          <button onClick={submit} disabled={busy || !title.trim()}
            className="inline-flex items-center gap-1.5 bg-brand-500 hover:bg-brand-600 text-white text-[12.5px] font-semibold px-4 py-2 rounded-full disabled:opacity-50 transition-colors whitespace-nowrap">
            <Send size={14} />{busy ? 'Sending…' : 'Submit request'}
          </button>
        </div>
      </div>

      <Card>
        <MonoLabel className="mb-4">Request pipeline</MonoLabel>
        <div className="flex items-start overflow-x-auto pb-1">
          {PIPELINE.map((s, i) => (
            <React.Fragment key={s.label}>
              <div className="flex flex-col items-center text-center shrink-0 w-[68px]">
                <span className="w-9 h-9 rounded-full grid place-items-center text-[13px] font-bold bg-brand-50 text-brand-600 border-2 border-brand-200">{i + 1}</span>
                <div className="text-[11.5px] font-semibold mt-1.5 leading-tight">{s.label}</div>
                <div className="text-[10px] text-gray-400">{s.who}</div>
              </div>
              <div className="flex-1 min-w-[14px] h-0.5 bg-gray-200 mt-4" />
            </React.Fragment>
          ))}
          <div className="flex flex-col items-center text-center shrink-0 w-[68px]">
            <span className="w-9 h-9 rounded-full grid place-items-center bg-gray-100 text-gray-400"><Boxes size={16} /></span>
            <div className="text-[11.5px] font-semibold mt-1.5 leading-tight">Live</div>
          </div>
        </div>
      </Card>
    </div>
  );

  const requestList = loading ? <Loading /> : error ? <ErrorNote message={error} /> : (
    <div className="bg-white border border-gray-100 rounded-xl overflow-hidden mb-5">
      <div className="px-5 py-3 border-b border-gray-100 font-semibold text-sm text-gray-900">{canDecide ? 'Requests in your scope' : 'My requests'}</div>
      {requests.length === 0 && <p className="px-5 py-6 text-[13px] text-gray-400">No requests yet.</p>}
      {requests.map((r) => (
            <div key={r.id} className="px-5 py-3.5 border-b border-gray-50 last:border-0">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[14px] font-medium text-gray-900">{r.title}</div>
                  {r.detail && <div className="text-[12.5px] text-gray-500 mt-0.5 whitespace-pre-line">{r.detail}</div>}
                  <div className="text-[11px] text-gray-400 mt-1">{r.requester_email}{r.department ? ` · ${r.department}` : ''}</div>
                  {/* Who it is with right now. Derived from the gate, so it is
                      right the day after a department head changes. A settled
                      request is with nobody and says nothing. */}
                  {r.reviewer_email && (
                    <div className="mt-1 text-[11px] text-gray-400">
                      With {r.reviewer_email} for the {r.reviewer_gate === 'department' ? 'department' : 'company'} decision
                    </div>
                  )}
                  {/* The two jobs on a request. Scoping fills in what the first
                      ask could not say; building delivers it. Both are handed
                      over the same way, which is why they draw the same. */}
                  <RosterLine req={r} role="scoper" canStaff={canStaffScoping}
                    onAdd={openStaffing} onHand={openStaffing} onDrop={drop} busy={busy} />
                  <RosterLine req={r} role="builder" canStaff={isCompany}
                    onAdd={openStaffing} onHand={openStaffing} onDrop={drop} busy={busy} />
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <ShapePill deliverable={r.deliverable} reason={r.deliverable_reason} />
                  <RiskPill risk={r.risk} />
                  <StatusPill status={r.status} />
                </div>
              </div>
              {(() => {
                const acts = actionsFor(r);
                return acts.length > 0 && (
                  <div className="flex gap-2 mt-2.5">
                    {acts.map((d) => (
                      <button key={d} disabled={busy} onClick={() => onAct(r, d)}
                        className={`text-[11.5px] font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50 ${DECISION_BTN[d].cls}`}>{decisionLabel(r, d)}</button>
                    ))}
                  </div>
                );
              })()}
              {r.status === 'approved' && (hints[r.id]?.match_count ?? 0) > 0 && (
                <div className="mt-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-[12px] text-emerald-700">
                  💡 May already exist in the marketplace{hints[r.id]?.top_match ? `: ${hints[r.id]?.top_match}` : ''} — consider reusing before building.
                </div>
              )}
              {/* Scoping is the Delivery Project path. A Quick Win is built by
                  the named expert and published against the request, so it has
                  no scoping card to open; offering the link anyway sent people
                  to a page that told them their own work did not exist. Older
                  Quick Wins still carry a card from before that rule, so the
                  route is checked rather than only the presence of an id. */}
              {r.status === 'approved' && r.problem_id && r.route !== 'quick_win' && mayScope(r) && (
                <button
                  onClick={() => carryOnScoping(r)}
                  disabled={crossing}
                  className="inline-flex items-center gap-1 mt-2.5 text-[12px] font-semibold text-brand-600 hover:text-brand-700 disabled:opacity-50">
                  Continue scoping <ArrowRight size={13} />
                </button>
              )}
              {r.tool_skill_id && (
                <div className="mt-2 px-3 py-2 rounded-lg bg-brand-50 border border-brand-200 text-[12px] text-brand-700">
                  ✅ Tool delivered —{' '}
                  <FeatureLink to={canDecide ? '/aai/skills' : '/my-tools'} className="font-semibold underline hover:text-brand-800">
                    {canDecide ? 'see it in the skills library' : 'find it in My Tools'}
                  </FeatureLink>
                </div>
              )}
            </div>
          ))}
        </div>
  );

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader
        title={canDecide ? 'Approval queue' : 'Request something'}
        subtitle={canDecide
          ? 'Read each one and pass it on, or send it back for more detail. Your yes is the first of two.'
          : 'Tell us what would help. Your department head sees it first, then your AI champion decides.'}
      />

      {/* Managers (dept head / champion / owner) lead with the queue; everyone
          else leads with the request form. */}
      {canDecide ? <>{requestList}{requestForm}</> : <>{requestForm}{requestList}</>}

      {approving && (
        <div className="fixed inset-0 z-[195] bg-black/30 flex items-center justify-center p-4" onClick={() => !busy && setApproving(null)}>
          <div className="bg-white rounded-xl p-5 w-full max-w-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-base font-semibold text-gray-900 mb-1">Approve use case</h2>
            <p className="text-[12.5px] text-gray-500 mb-4">Recorded on your use-case registry.</p>
            <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1">Model</label>
            <select value={mdl} onChange={(e) => setMdl(e.target.value)} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm mb-3">
              {MODEL_OPTS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1">Human-in-loop</label>
            <select value={hil} onChange={(e) => setHil(e.target.value)} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm mb-3">
              {HIL_OPTS.map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
            {/* I2: naming someone turns the PRD-ready ping from a broadcast to
                every expert into a message to one person, and gives the
                requester an answer to "who has it". Optional: leaving it blank
                keeps the old behaviour rather than blocking the approval. */}
            <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1">Who will build it</label>
            <select value={assignee} onChange={(e) => setAssignee(e.target.value)} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm mb-1">
              <option value="">Decide later</option>
              {experts.map((x) => <option key={x.email} value={x.email}>{x.name}</option>)}
            </select>
            <p className="text-[11.5px] text-gray-400 mb-4">
              {experts.length === 0
                ? 'Nobody on the roster is set up to take this yet.'
                : 'They are told, and the requester can see who has it.'}
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setApproving(null)} disabled={busy} className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 disabled:opacity-50">Cancel</button>
              <button onClick={() => approving && decide(approving.id, 'approved', { model: mdl, human_in_loop: hil, assigned_expert_email: assignee || undefined })} disabled={busy} className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-brand-500 hover:bg-brand-600 text-white disabled:opacity-50">{busy ? 'Approving…' : 'Approve'}</button>
            </div>
          </div>
        </div>
      )}

      {assigning && (
        <div className="fixed inset-0 z-[195] bg-black/30 flex items-center justify-center p-4" onClick={() => !busy && closeAssign()}>
          <div className="bg-white rounded-xl p-5 w-full max-w-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-base font-semibold text-gray-900 mb-1">{staffingTitle}</h2>
            <p className="text-[12.5px] text-gray-500 mb-4">{staffingNote}</p>
            <select value={assignee} onChange={(e) => setAssignee(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm mb-4">
              <option value="">Pick someone</option>
              {experts.map((x) => <option key={x.email} value={x.email}>{x.name}</option>)}
            </select>
            {experts.length === 0 && (
              <p className="text-[12px] text-amber-700 mb-4">
                Nobody on the roster can take this yet. Add an Expert on the Team page first.
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={closeAssign} disabled={busy}
                className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 disabled:opacity-50">Cancel</button>
              <button onClick={() => assigning && staff(assigning.id, assignee)}
                disabled={busy || !assignee}
                className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-brand-500 hover:bg-brand-600 text-white disabled:opacity-50">
                {busy ? 'Saving…' : staffingAction}
              </button>
            </div>
          </div>
        </div>
      )}

      {reasoning && (
        <div className="fixed inset-0 z-[195] bg-black/30 flex items-center justify-center p-4" onClick={() => !busy && setReasoning(null)}>
          <div className="bg-white rounded-xl p-5 w-full max-w-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-base font-semibold text-gray-900 mb-1">
              {reasoning.decision === 'declined' ? 'Decline request' : 'Ask for more detail'}
            </h2>
            <p className="text-[12.5px] text-gray-500 mb-3">
              {reasoning.decision === 'declined'
                ? 'The requester is notified with your reason.'
                : 'The requester is notified with what you need.'}
            </p>
            <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1">
              Reason {reasoning.decision === 'declined' ? '(required)' : '(optional)'}
            </label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} autoFocus
              placeholder={reasoning.decision === 'declined' ? 'Why is this being declined?' : 'What extra detail do you need?'}
              className="w-full rounded-lg border border-gray-200 p-3 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-brand-200" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setReasoning(null)} disabled={busy} className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 disabled:opacity-50">Cancel</button>
              <button onClick={() => decide(reasoning.req.id, reasoning.decision, { comment: reason.trim() || undefined })}
                disabled={busy || (reasoning.decision === 'declined' && !reason.trim())}
                className={`text-[12px] font-semibold px-3 py-1.5 rounded-lg text-white disabled:opacity-50 ${reasoning.decision === 'declined' ? 'bg-red-600 hover:bg-red-700' : 'bg-brand-500 hover:bg-brand-600'}`}>
                {busy ? 'Saving…' : reasoning.decision === 'declined' ? 'Decline' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestsPage;
